/*
  Brilinton PDP feature lifecycle owner — R44-264.

  Responsibilities kept here are legacy PDP enhancements that do not yet have
  their own custom element. Every initializer is idempotent and section-scoped.
  Broad document mutation observation and product-spec accordion ownership were
  intentionally removed: product-spec sections own their own accordion state.
*/
(function () {
  'use strict';

  var GLOBAL_KEY = '__brilintonPdpFeaturesR44264';
  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].refresh === 'function') {
    window[GLOBAL_KEY].refresh(document);
    return;
  }

  var responsiveMediaItems = new Set();
  var resizeTimer = 0;
  var globalListenersBound = false;
  var recentlyViewedStored = false;

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function debounce(fn, wait) {
    var timeout;
    return function () {
      var context = this;
      var args = arguments;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        fn.apply(context, args);
      }, wait || 100);
    };
  }

  function nodesWithin(scope, selector) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = [];
    if (root.nodeType === 1 && root.matches && root.matches(selector)) nodes.push(root);
    return nodes.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
  }

  function initImageComparisons(scope) {
    nodesWithin(scope, '[data-component="image-comparison-slider"]').forEach(function (comparison) {
      if (comparison.closest('[data-image-comparison-local-owner="true"]')) return;

      if (comparison.__brImageComparisonController) {
        comparison.__brImageComparisonController.sync();
        return;
      }

      var sliderRange = comparison.querySelector('[data-image-comparison-range]');
      var slider = comparison.querySelector('[data-image-comparison-slider]');
      var imageWrapperOverlay = comparison.querySelector('[data-image-comparison-overlay]');
      var thumb = comparison.querySelector('[data-image-comparison-thumb]');
      var wrapper = comparison.querySelector('.image-comparison__slider-wrapper');
      var overlayImage = comparison.querySelector('.image-comparison__figure--overlay .image-comparison__image');
      var resizeObserver = null;
      var imageListeners = [];
      var mouseTracking = false;

      function sync() {
        if (!comparison.isConnected || !wrapper || !overlayImage) return;
        var rect = wrapper.getBoundingClientRect ? wrapper.getBoundingClientRect() : null;
        var width = rect && rect.width ? rect.width : wrapper.offsetWidth;
        if (width) overlayImage.style.width = Math.round(width) + 'px';
      }

      function stopMouseTracking() {
        if (!mouseTracking) return;
        mouseTracking = false;
        comparison.removeEventListener('mousemove', moveThumb);
      }

      function moveThumb(event) {
        if (!sliderRange || !thumb) return;
        var rect = sliderRange.getBoundingClientRect ? sliderRange.getBoundingClientRect() : null;
        var pointerY = rect ? event.clientY - rect.top : event.layerY;
        var position = pointerY - 20;
        if (pointerY <= 0) position = -20;
        if (pointerY >= sliderRange.offsetHeight) position = sliderRange.offsetHeight - 20;
        thumb.style.top = position + 'px';
      }

      function startMouseTracking(event) {
        if (!sliderRange || !thumb) return;
        moveThumb(event);
        if (!mouseTracking) {
          mouseTracking = true;
          comparison.addEventListener('mousemove', moveThumb);
        }
      }

      function updateFromRange(event) {
        if (!sliderRange) return;
        var value = event.target.value;
        if (slider) slider.style.left = value + '%';
        if (imageWrapperOverlay) imageWrapperOverlay.style.width = value + '%';
        sliderRange.classList.toggle('image-comparison__range--active', event.type === 'input');
        if (event.type !== 'input') stopMouseTracking();
        sync();
      }

      if (sliderRange) {
        sliderRange.addEventListener('input', updateFromRange);
        sliderRange.addEventListener('change', updateFromRange);
        if (!('ontouchstart' in window)) {
          sliderRange.addEventListener('mousedown', startMouseTracking);
          sliderRange.addEventListener('mouseup', stopMouseTracking);
          sliderRange.addEventListener('mouseleave', stopMouseTracking);
        }
      }

      comparison.querySelectorAll('img').forEach(function (image) {
        var onLoad = function () {
          sync();
          window.requestAnimationFrame(sync);
        };
        image.addEventListener('load', onLoad);
        imageListeners.push([image, onLoad]);
      });

      if ('ResizeObserver' in window && wrapper) {
        resizeObserver = new ResizeObserver(sync);
        resizeObserver.observe(wrapper);
      }

      comparison.__brImageComparisonController = {
        sync: sync,
        destroy: function () {
          stopMouseTracking();
          if (resizeObserver) resizeObserver.disconnect();
          if (sliderRange) {
            sliderRange.removeEventListener('input', updateFromRange);
            sliderRange.removeEventListener('change', updateFromRange);
            sliderRange.removeEventListener('mousedown', startMouseTracking);
            sliderRange.removeEventListener('mouseup', stopMouseTracking);
            sliderRange.removeEventListener('mouseleave', stopMouseTracking);
          }
          imageListeners.forEach(function (entry) {
            entry[0].removeEventListener('load', entry[1]);
          });
          delete comparison.__brImageComparisonController;
        }
      };

      sync();
      window.requestAnimationFrame(sync);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(sync).catch(function () {});
      }
    });
  }


  function initStickyAddToCart(scope) {
    var searchRoot = scope && scope.querySelector ? scope : document;
    var stickyBtn = (searchRoot.nodeType === 1 && searchRoot.id === 'custom-add-to-cart')
      ? searchRoot
      : searchRoot.querySelector('#custom-add-to-cart') || document.getElementById('custom-add-to-cart');
    var nextRealBtn = document.querySelector('.product-form__submit');
    if (!stickyBtn || !nextRealBtn) return;

    if (stickyBtn.__brStickyAtcController) {
      stickyBtn.__brStickyAtcController.refresh(nextRealBtn);
      return;
    }

    var realBtn = null;
    var intersectionObserver = null;
    var mutationObserver = null;

    function isDisabled(element) {
      if (!element) return true;
      if (element.tagName === 'BUTTON') return element.disabled;
      if (element.tagName === 'A') {
        return element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true' || element.classList.contains('disabled') || element.classList.contains('is-disabled');
      }
      return false;
    }

    function syncButtonState(isRealButtonVisible) {
      if (!realBtn) return;
      if (typeof isRealButtonVisible === 'boolean') stickyBtn.classList.toggle('hidden', isRealButtonVisible);
      var disabled = isDisabled(realBtn);
      stickyBtn.toggleAttribute('disabled', disabled);
      stickyBtn.classList.toggle('disabled', disabled);

      var realLabel = realBtn.firstElementChild && realBtn.firstElementChild.tagName === 'SPAN'
        ? realBtn.firstElementChild
        : realBtn.querySelector('span');
      var text = realBtn.textContent && realBtn.textContent.trim();
      if (realLabel && realLabel.innerHTML.trim()) stickyBtn.innerHTML = realLabel.innerHTML;
      else if (text) stickyBtn.textContent = text;
    }

    function disconnectRealButtonObservers() {
      if (intersectionObserver) intersectionObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      intersectionObserver = null;
      mutationObserver = null;
    }

    function bindRealButton(button) {
      if (!button) return;
      if (realBtn === button) {
        syncButtonState();
        return;
      }

      disconnectRealButtonObservers();
      realBtn = button;

      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { syncButtonState(entry.isIntersecting); });
        });
        intersectionObserver.observe(realBtn);
      } else {
        syncButtonState(false);
      }

      mutationObserver = new MutationObserver(function () { syncButtonState(); });
      mutationObserver.observe(realBtn, { attributes: true, childList: true, characterData: true, subtree: true });
      syncButtonState();
    }

    function onClick(event) {
      if (!realBtn || isDisabled(realBtn)) {
        event.preventDefault();
        return;
      }
      if (realBtn.tagName === 'BUTTON') realBtn.click();
      else if (realBtn.tagName === 'A' && realBtn.href) window.location.href = realBtn.href;
    }

    stickyBtn.addEventListener('click', onClick);
    stickyBtn.__brStickyAtcController = {
      refresh: bindRealButton,
      destroy: function () {
        disconnectRealButtonObservers();
        stickyBtn.removeEventListener('click', onClick);
        realBtn = null;
        delete stickyBtn.__brStickyAtcController;
      }
    };
    bindRealButton(nextRealBtn);
  }

  function initFlickitySliders(scope) {
    if (typeof window.Flickity === 'undefined') return;
    nodesWithin(scope, '.multi-slider_container').forEach(function (element) {
      if (element.classList.contains('b1-flickity-initialized') || element.classList.contains('flickity-enabled')) return;
      element.classList.add('b1-flickity-initialized');
      new window.Flickity(element, {
        cellAlign: 'center',
        contain: true,
        pageDots: false,
        wrapAround: true,
        percentPosition: true
      });
    });
  }

  function initMulticolumnPopupDelegation() {
    if (window.__brMulticolumnPopupDelegationBound) return;
    window.__brMulticolumnPopupDelegationBound = true;
    document.addEventListener('click', function (event) {
      var opener = event.target.closest && event.target.closest('button.multicolumn-card_opener');
      if (!opener) return;
      var card = opener.parentElement && opener.parentElement.parentElement;
      if (card) card.classList.toggle('active');
    });
  }

  function initMulticolumnTabs(scope) {
    nodesWithin(scope, '.multicolumn-tab').forEach(function (section) {
      if (section.dataset.brMulticolumnTabsBound === 'true') return;
      section.dataset.brMulticolumnTabsBound = 'true';
      var tabButtons = Array.prototype.slice.call(section.querySelectorAll('.tab'));
      var tabParents = Array.prototype.slice.call(section.querySelectorAll('[data-tab-parent]'));
      if (!tabButtons.length || !tabParents.length) return;

      if (!tabButtons.some(function (button) { return button.classList.contains('active'); })) {
        var defaultButton = tabButtons[0];
        var defaultParent = section.querySelector('[data-tab-parent="' + defaultButton.getAttribute('data-tab') + '"]');
        defaultButton.classList.add('active');
        if (defaultParent) defaultParent.classList.add('active', 'fade-in');
      }

      tabButtons.forEach(function (button) {
        if (button.dataset.brMulticolumnTabBound === 'true') return;
        button.dataset.brMulticolumnTabBound = 'true';
        button.addEventListener('click', function () {
          var targetTab = button.getAttribute('data-tab');
          var wasActive = button.classList.contains('active');
          tabButtons.forEach(function (item) { item.classList.remove('active'); });
          tabParents.forEach(function (item) { item.classList.remove('active', 'fade-in'); });
          if (!wasActive) {
            button.classList.add('active');
            var target = section.querySelector('[data-tab-parent="' + targetTab + '"]');
            if (target) target.classList.add('active', 'fade-in');
          }
        });
      });
    });
  }

  function registerResponsiveMulticolumnMedia(scope) {
    nodesWithin(scope, '.multicolumn-without__media').forEach(function (element) {
      if (element.__brResponsiveMedia) return;
      var parent = element.parentElement;
      if (!parent) return;
      element.__brResponsiveMedia = {
        parent: parent,
        nextSibling: element.nextSibling
      };
      responsiveMediaItems.add(element);
    });
    syncResponsiveMulticolumnMedia();
  }

  function syncResponsiveMulticolumnMedia() {
    var isSmallDevice = window.innerWidth < 749;
    responsiveMediaItems.forEach(function (element) {
      var state = element.__brResponsiveMedia;
      if (!state || !element.isConnected || !state.parent.isConnected) {
        responsiveMediaItems.delete(element);
        return;
      }
      if (isSmallDevice) {
        if (element.parentElement === state.parent && state.parent.parentElement) {
          state.parent.parentElement.insertBefore(element, state.parent);
        }
      } else if (element.parentElement !== state.parent) {
        var reference = state.nextSibling && state.nextSibling.parentElement === state.parent ? state.nextSibling : null;
        state.parent.insertBefore(element, reference);
      }
    });
  }

  function initVideoCards(scope) {
    /* R44-263: no synthetic poster clicks or automatic play attempts on boot. */
    nodesWithin(scope, '.multicolumn-card_video_play').forEach(function (control) {
      if (control.dataset.brVideoControlBound === 'true') return;
      control.dataset.brVideoControlBound = 'true';
      var host = control.parentElement;
      if (!host) return;

      function currentVideo() { return host.querySelector('video'); }
      function syncState() {
        var video = currentVideo();
        control.classList.toggle('active', Boolean(video && !video.paused));
      }
      function bindVideo(video) {
        if (!video || video.dataset.brVideoStateBound === 'true') return;
        video.dataset.brVideoStateBound = 'true';
        video.addEventListener('play', syncState);
        video.addEventListener('pause', syncState);
        video.addEventListener('ended', syncState);
      }

      bindVideo(currentVideo());
      control.addEventListener('click', function () {
        var video = currentVideo();
        var poster = host.querySelector('.video-section__poster');
        if (video) {
          bindVideo(video);
          if (video.paused) video.play().catch(function () {});
          else video.pause();
          return;
        }
        if (poster && typeof poster.click === 'function') {
          poster.click();
          window.requestAnimationFrame(function () {
            bindVideo(currentVideo());
            syncState();
          });
        }
      });
      syncState();
    });
  }

  function initVariantHoverLabels(scope) {
    nodesWithin(scope, 'variant-selects').forEach(function (variantSelect) {
      var group = variantSelect.querySelector('.product-form__input--pill');
      if (!group) return;
      var selectedValue = group.querySelector('[data-selected-value]');
      group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        if (radio.classList.contains('disabled')) return;
        var label = radio.nextElementSibling;
        if (!label || label.dataset.brVariantHoverBound === 'true') return;
        label.dataset.brVariantHoverBound = 'true';
        label.addEventListener('mouseenter', function () {
          if (!selectedValue) return;
          selectedValue.dataset.defaultValue = selectedValue.innerText;
          selectedValue.innerText = label.innerText;
        });
        label.addEventListener('mouseleave', function () {
          if (selectedValue && selectedValue.dataset.defaultValue) selectedValue.innerText = selectedValue.dataset.defaultValue;
        });
      });
    });
  }

  function storeRecentlyViewedProduct() {
    if (recentlyViewedStored || !window.localStorage) return;
    var productNode = document.querySelector('[data-product-id]');
    var productId = productNode && productNode.getAttribute('data-product-id');
    if (!productId) return;
    recentlyViewedStored = true;

    try {
      var raw = localStorage.getItem('recentlyViewed');
      var viewed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(viewed)) viewed = [];
      if (viewed.indexOf(productId) !== -1) return;
      viewed.unshift(productId);
      if (viewed.length > 10) viewed.length = 10;
      var save = function () { localStorage.setItem('recentlyViewed', JSON.stringify(viewed)); };
      if ('requestIdleCallback' in window) requestIdleCallback(save, { timeout: 1000 });
      else window.setTimeout(save, 0);
    } catch (error) {
      /* LocalStorage can be blocked in privacy modes. */
    }
  }

  function refresh(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    initImageComparisons(root);
    initStickyAddToCart(root);
    initFlickitySliders(root);
    initMulticolumnTabs(root);
    registerResponsiveMulticolumnMedia(root);
    initVideoCards(root);
    initVariantHoverLabels(root);
  }

  function destroyScope(scope) {
    if (!scope || !scope.querySelectorAll) return;
    nodesWithin(scope, '[data-component="image-comparison-slider"]').forEach(function (node) {
      node.__brImageComparisonController && node.__brImageComparisonController.destroy();
    });
    nodesWithin(scope, '#custom-add-to-cart').forEach(function (node) {
      node.__brStickyAtcController && node.__brStickyAtcController.destroy();
    });
    nodesWithin(scope, '.multicolumn-without__media').forEach(function (node) {
      responsiveMediaItems.delete(node);
      delete node.__brResponsiveMedia;
    });
  }

  function bindGlobalLifecycle() {
    if (globalListenersBound) return;
    globalListenersBound = true;
    initMulticolumnPopupDelegation();

    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        initImageComparisons(document);
        syncResponsiveMulticolumnMedia();
      }, 120);
    });

    window.addEventListener('brilinton:layout-refresh', function () {
      refresh(document);
    });

    document.addEventListener('brilinton:pdp-section-refreshed', function (event) {
      refresh(event.target || document);
    });

    document.addEventListener('shopify:section:load', function (event) {
      refresh(event.target || document);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      destroyScope(event.target);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        window.requestAnimationFrame(function () { refresh(document); });
      });
    }
  }

  var api = {
    refresh: refresh,
    destroyScope: destroyScope
  };
  window[GLOBAL_KEY] = api;
  window.BrilintonPdpFeatures = api;

  onReady(function () {
    bindGlobalLifecycle();
    refresh(document);
    storeRecentlyViewedProduct();
  });
})();
